import React, { Suspense, lazy, createRef } from 'react'
import { Grid, Box, Backdrop } from '@mui/material'
import { withStyles, WithStyles } from '@mui/styles'
import queryString from 'query-string'
import { connect } from 'react-redux'
import { bindActionCreators, Dispatch } from 'redux'
import clsx from 'clsx'
import { ReCAPTCHA } from 'react-google-recaptcha'

import { login, clearBackendValidation } from 'ducks/account/actions'
import { ReduxState } from 'ducks/types'
import AccountApi from 'apis/accountApi'
import i18n from 'i18n/index'
import { enterKeyText, enterKeyValue } from 'constants/globals'
import ReleaseNotes from './ReleaseNotes'
import { Props, State } from './types'
import useStyles from './style'
import LogoLogin from './LogoLogin'
import BottomLinks from './BottomLinks'
import FormLogin from './FormLogin'
import FromForgotPassword from './FromForgotPassword'
import RecoverPassword from './RecoverPassword'

import REGEX_EMAIL from './constants'
import tutorialSteps from './ReleaseNotes/constants'
import history from 'utils/browserHistory'
import Zendesk from 'components/Zendesk'
import { KEY_STORAGE_LAST_PATH, PATH_TERMINAL } from 'routes/paths'

const ProgressBar = lazy(() => import('components/Common/ProgressBar'))
const Loader = lazy(() => import('components/Common/Loader'))

class LoginContainer extends React.Component<Props & WithStyles<typeof useStyles>, State> {
  state = {
    credentials: {
      username: '',
      password: ''
    },
    isLoaded: false,
    showLoader: false,
    showForgotPassword: false,
    emailForgotPassword: '',
    sendValidate: false,
    finishRecoverPassword: false,
    tokenReset: '',
    backgroundLinearGradient: tutorialSteps[0].background,
    errorRegister: false
  }
  captchaRef = createRef<{ current: ReCAPTCHA; getValue: () => string }>()

  handleEnterKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (this.state.showForgotPassword) return
    if (this.props.error !== '') {
      this.props.clearBackendValidation()
    }

    if (e.key === enterKeyText || e.keyCode === enterKeyValue) {
      this.login()
    }
  }

  changeUsername = (value: string) => {
    this.setState((prevState) => ({
      credentials: {
        ...prevState.credentials,
        username: value
      }
    }))
  }

  login = async () => {
    if (!REGEX_EMAIL.test(this.state.credentials.username)) return
    let tokenRecaptcha = ''
    const validateTokenCaptcha = Boolean(localStorage.getItem('errorLogin'))
    if (this.captchaRef && this.captchaRef.current && validateTokenCaptcha) {
      tokenRecaptcha = this.captchaRef.current.getValue() || ''
    }
    if (!tokenRecaptcha && localStorage.getItem('errorLogin')) return
    this.setState({ showLoader: true })
    if (this.props.error !== '') {
      this.props.clearBackendValidation()
    }
    const resLogin: any = await this.props.login(this.state.credentials)
    if (resLogin?.status === 401) {
      this.setState({ showLoader: false })
      return
    }
    history.go(localStorage.getItem(KEY_STORAGE_LAST_PATH) || PATH_TERMINAL, { state: { fromDashboard: true } })
    this.setState({ showLoader: false })
  }

  handlefinishRecoverPassword = async () => {
    this.setState({
      sendValidate: false,
      finishRecoverPassword: false,
      showForgotPassword: false,
      errorRegister: false
    })
  }

  forgotPassword = async () => {
    let tokenRecaptcha = ''
    if (this.captchaRef && this.captchaRef.current) {
      tokenRecaptcha = this.captchaRef.current.getValue() || ''
    }
    if (!tokenRecaptcha) return
    const validateEmail = REGEX_EMAIL.test(this.state.emailForgotPassword)
    if (this.state.emailForgotPassword && !validateEmail) {
      this.setState({ sendValidate: true })
      return
    }
    try {
      const responseForgot = await AccountApi.forgotPassword(this.state.emailForgotPassword)
      if (String(responseForgot).includes(String('TypeError: Failed to fetch'))) {
        this.setState({ sendValidate: true, finishRecoverPassword: false, errorRegister: true })
        return
      }
      this.setState({ sendValidate: true, finishRecoverPassword: true })
      // eslint-disable-next-line no-empty
    } catch (err) {}
  }

  handleLoading = (status: boolean): void => {
    this.setState({
      isLoaded: status
    })
  }

  handleShowForgotPassword = (): void => {
    this.setState({
      emailForgotPassword: '',
      showForgotPassword: !this.state.showForgotPassword
    })
  }

  validateErrorRecoverPassword = (): string => {
    const validateEmail = REGEX_EMAIL.test(this.state.emailForgotPassword)
    if (this.state.emailForgotPassword && this.state.sendValidate && !validateEmail) {
      return i18n.t('emailValid')
    }
    return ''
  }

  changeEmail = (value: string): void => {
    this.setState({
      emailForgotPassword: value
    })
  }

  handlePassword = (password: string): void => {
    this.setState((prevState) => ({
      credentials: {
        ...prevState.credentials,
        password
      }
    }))
  }

  setToken = (newtoken: string) => {
    this.setState({
      tokenReset: newtoken
    })
  }

  handleQueryString = () => {
    if (history.location) {
      const queries: { token?: string } = queryString.parse(history.location.search)

      if (queries && queries.token) {
        this.handleLoading(true)
        this.setState({ showLoader: true })
        this.setToken(queries.token)
      }
    }
  }

  componentDidMount = (): void => {
    this.handleQueryString()
  }

  handleBackground = (newGradient: string): void => {
    this.setState({ backgroundLinearGradient: newGradient || '' })
  }

  getLoginComponent = () => {
    if (this.state.tokenReset)
      return (
        <RecoverPassword
          token={this.state.tokenReset}
          setToken={this.setToken}
          handleLoading={(loading: boolean) => this.setState({ showLoader: loading })}
        />
      )
    return !this.state.showForgotPassword ? (
      <FormLogin
        changeUsername={this.changeUsername}
        handleEnterKey={this.handleEnterKey}
        handlePassword={this.handlePassword}
        handleShowForgotPassword={this.handleShowForgotPassword}
        error={this.props.error}
        login={this.login}
        credentials={this.state.credentials}
        captchaRef={this.captchaRef as any}
      />
    ) : (
      <FromForgotPassword
        handleEnterKey={this.handleEnterKey}
        changeEmail={this.changeEmail}
        handleShowForgotPassword={this.handleShowForgotPassword}
        email={this.state.emailForgotPassword}
        forgotPassword={this.forgotPassword}
        error={this.validateErrorRecoverPassword()}
        handlefinishRecoverPassword={this.handlefinishRecoverPassword}
        finishRecoverPassword={this.state.finishRecoverPassword}
        captchaRef={this.captchaRef as any}
        errorRegister={this.state.errorRegister}
      />
    )
  }

  render() {
    const { classes } = this.props
    const { isLoaded, showLoader, backgroundLinearGradient } = this.state

    return (
      <Suspense fallback={<Box />}>
        <Box height="100vh" display="flex" flexDirection="column">
          {isLoaded ? (
            <>
              <Grid container direction="row" justifyContent="center" className={classes.container}>
                <Grid
                  item
                  xs={12}
                  md={8}
                  lg={8}
                  className={clsx(classes.backgoundRightContainer, 'fontReleaseNote')}
                  style={{ background: backgroundLinearGradient || '' }}
                  id="container-release-notes"
                >
                  <ReleaseNotes handleBackground={this.handleBackground} />
                </Grid>
                <Grid item xs={12} md={4} lg={4} className={classes.leftContainer} sx={{ bgcolor: 'background.paper' }}>
                  <Grid item xs={12} className={classes.formLogin}>
                    <LogoLogin />
                    <Box id="container-login-form" className={classes.logoContainer} />
                    {this.getLoginComponent()}
                  </Grid>
                  <Grid
                    container
                    justifyContent="space-between"
                    alignItems="center"
                    style={{ paddingBottom: 25 }}
                    spacing={1}
                  >
                    <BottomLinks />
                  </Grid>
                </Grid>
              </Grid>
              <Backdrop className={classes.backdrop} open={showLoader}>
                <Loader />
              </Backdrop>
              <Box sx={{ position: 'fixed', bottom: 15, right: 5 }}>
                <Zendesk />
              </Box>
            </>
          ) : (
            <ProgressBar
              loadingCompleted={(status: boolean) => {
                this.handleLoading(status)
              }}
            />
          )}
        </Box>
      </Suspense>
    )
  }
}

const mapStateToProps = (state: ReduxState) => ({
  error: state.account.error
})

const mapDispatchToProps = (dispatch: Dispatch) => {
  return bindActionCreators({ login, clearBackendValidation }, dispatch)
}

const ConnectedComponent = connect(mapStateToProps, mapDispatchToProps)(LoginContainer)

export default withStyles(useStyles, { withTheme: true })(ConnectedComponent)
